import { Badge, Button, Checkbox, Label, Textarea } from "@devhop/ui";
import { ChevronDown, RotateCcw, Save } from "lucide-react";
import { useEffect, useState } from "react";
import {
  useUpsertNotificationSettings,
  useUpsertStatusEventTemplate,
} from "../api/queries";
import type {
  CustomerStatusEventType,
  MessengerNotificationSettings,
  StatusEventTemplateItem,
  TemplateVariableInfo,
} from "../api/types";
import { VariableChipList } from "./VariableChipList";

const STATUS_OPTIONS: Array<{
  value: CustomerStatusEventType;
  label: string;
}> = [
  { value: "order_confirmed", label: "ຢືນຢັນຄຳສັ່ງຊື້" },
  { value: "order_updated", label: "ຂໍ້ມູນສິນຄ້າຖືກອັບເດດ" },
  { value: "managing_started", label: "ເລີ່ມຈັດການຄຳສັ່ງຊື້" },
  { value: "packing_started", label: "ເລີ່ມແພັກສິນຄ້າ" },
  { value: "packing_completed", label: "ແພັກສິນຄ້າສຳເລັດ / ລໍຖ້າຂົນສົ່ງ" },
  { value: "delivery_job_created", label: "ສ້າງໃບສົ່ງສິນຄ້າ" },
  { value: "delivery_status_updated", label: "ສະຖານະການຈັດສົ່ງຖືກອັບເດດ" },
  { value: "carrier_handoff", label: "ສົ່ງມອບໃຫ້ຜູ້ຂົນສົ່ງ" },
  { value: "order_delivered", label: "ສົ່ງມອບລູກຄ້າແລ້ວ" },
  { value: "hal_accepted", label: "HAL ຮັບພັດສະດຸ" },
  { value: "hal_sorting", label: "HAL ກຳລັງຄັດແຍກພັດສະດຸ" },
  { value: "hal_in_transit", label: "HAL ກຳລັງຂົນສົ່ງ" },
  { value: "hal_bill_ready", label: "ບິນ HAL ພ້ອມແລ້ວ" },
  { value: "tracking_image_added", label: "ມີຮູບ Tracking ໃໝ່" },
  { value: "hal_arrived_destination", label: "ພັດສະດຸຮອດປາຍທາງ" },
  { value: "hal_collected", label: "ລູກຄ້າຮັບພັດສະດຸ" },
  { value: "cancelled", label: "ຍົກເລີກຄຳສັ່ງຊື້" },
  { value: "returned", label: "ສິນຄ້າຖືກສົ່ງຄືນ" },
];

type Props = {
  pageId: string;
  settings: MessengerNotificationSettings;
  statusEventTemplates: StatusEventTemplateItem[];
  statusUpdateFallback: string;
  variables: TemplateVariableInfo[];
};

export function StatusNotificationSettingsCard({
  pageId,
  settings,
  statusEventTemplates,
  statusUpdateFallback,
  variables,
}: Props) {
  const [enabled, setEnabled] = useState<CustomerStatusEventType[]>(
    settings.enabledStatusEvents,
  );
  const [expanded, setExpanded] = useState<CustomerStatusEventType | null>(
    null,
  );
  const [drafts, setDrafts] = useState<
    Partial<Record<CustomerStatusEventType, string>>
  >({});
  const saveSettings = useUpsertNotificationSettings(pageId);
  const saveOverride = useUpsertStatusEventTemplate(pageId);

  useEffect(() => {
    setEnabled(settings.enabledStatusEvents);
  }, [settings.enabledStatusEvents]);

  useEffect(() => {
    const next: Partial<Record<CustomerStatusEventType, string>> = {};
    for (const item of statusEventTemplates) {
      next[item.eventType] = item.content;
    }
    setDrafts(next);
  }, [statusEventTemplates]);

  const toggle = (value: CustomerStatusEventType, checked: boolean) => {
    setEnabled((current) =>
      checked
        ? STATUS_OPTIONS.map((option) => option.value).filter(
            (eventType) => eventType === value || current.includes(eventType),
          )
        : current.filter((eventType) => eventType !== value),
    );
  };

  const dirtySettings =
    enabled.length !== settings.enabledStatusEvents.length ||
    enabled.some(
      (eventType) => !settings.enabledStatusEvents.includes(eventType),
    );

  const byEvent = new Map(
    statusEventTemplates.map((item) => [item.eventType, item] as const),
  );

  const statusVariables = variables;

  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-medium">ສະຖານະທີ່ສົ່ງຫາລູກຄ້າ</h3>
          <p className="mt-1 text-muted-foreground text-sm">
            ເປີດ/ປິດການສົ່ງຕໍ່ stage ແລະ ແກ້ຂໍ້ຄວາມແຍກໄດ້ — ຖ້າບໍ່ແກ້ຈະໃຊ້ template
            ອັບເດດສະຖານະດ້ານລຸ່ມ
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!dirtySettings || saveSettings.isPending}
          onClick={() => saveSettings.mutate(enabled)}
        >
          <span className="inline-flex items-center gap-2">
            <Save className="size-4" aria-hidden />
            ບັນທຶກການເປີດສົ່ງ
          </span>
        </Button>
      </div>
      <div className="mt-4 grid gap-3">
        {STATUS_OPTIONS.map((option) => {
          const id = `status-event-${option.value}`;
          const item = byEvent.get(option.value);
          const isOpen = expanded === option.value;
          const draft =
            drafts[option.value] ?? item?.content ?? statusUpdateFallback;
          const isOverride = item?.isOverride ?? false;
          const draftDirty = draft !== (item?.content ?? statusUpdateFallback);

          return (
            <div key={option.value} className="rounded-lg border">
              <div className="flex items-center gap-3 p-3">
                <Checkbox
                  id={id}
                  checked={enabled.includes(option.value)}
                  onCheckedChange={(checked) =>
                    toggle(option.value, checked === true)
                  }
                />
                <Label htmlFor={id} className="flex-1 cursor-pointer text-sm">
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {option.label}
                    {isOverride ? (
                      <Badge variant="secondary">ຂໍ້ຄວາມສະເພາະ</Badge>
                    ) : null}
                  </span>
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setExpanded((current) =>
                      current === option.value ? null : option.value,
                    )
                  }
                >
                  <span className="inline-flex items-center gap-1">
                    ແກ້ຂໍ້ຄວາມ
                    <ChevronDown
                      className={`size-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                  </span>
                </Button>
              </div>
              {isOpen ? (
                <div className="space-y-3 border-t px-3 py-3">
                  <VariableChipList
                    kind="status_update"
                    variables={statusVariables}
                    onInsert={(key) => {
                      const token = `{{${key}}}`;
                      setDrafts((current) => ({
                        ...current,
                        [option.value]: `${current[option.value] ?? draft}${token}`,
                      }));
                    }}
                  />
                  <Textarea
                    rows={5}
                    value={draft}
                    onChange={(e) =>
                      setDrafts((current) => ({
                        ...current,
                        [option.value]: e.target.value,
                      }))
                    }
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={!draftDirty || saveOverride.isPending}
                      onClick={() =>
                        saveOverride.mutate({
                          eventType: option.value,
                          content: draft,
                        })
                      }
                    >
                      <span className="inline-flex items-center gap-2">
                        <Save className="size-4" aria-hidden />
                        ບັນທຶກຂໍ້ຄວາມ
                      </span>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!isOverride || saveOverride.isPending}
                      onClick={() =>
                        saveOverride.mutate({
                          eventType: option.value,
                          content: "",
                        })
                      }
                    >
                      <span className="inline-flex items-center gap-2">
                        <RotateCcw className="size-4" aria-hidden />
                        ໃຊ້ຄ່າເລີ່ມຕົ້ນ
                      </span>
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
